import { createContext, PropsWithChildren, useEffect, useState } from 'react'

import { SearchEntryDto } from '../../_generated/api/search'
import { useApiContext } from './ApiContext'
import { useUserContext } from './UserContext'
import { useProvideContext } from '../../hooks/context'
import { useImmutableList } from '../../hooks/immutable'
import Bolt from '../../models/Bolt'
import SearchResult from '../../models/SearchResult'
import { mapModelList, ok } from '../../tools/api'
import { ApiModelListStorage } from '../../tools/dom/storage'
import { StructureTypes } from '../../models/Structure'

export interface SearchContextValue {
    debouncedSearch: (
        searchTerm: string,
        filters?: string[],
        labels?: string[],
        start?: Date,
        end?: Date
    ) => void
    rawSearch: (request: RawSearchRequest) => Promise<SearchResult[]>
    resetSearch: () => void
    recentSearches: SearchResult[]
    storeRecentSearch: (result: SearchResult) => void
    deleteRecentSearch: (result: SearchResult) => void
    cleanupRecentSearches: () => void
    typeFilters: string[]
    searchResults: SearchResult[]
    loading: boolean
    error: boolean
}

export interface RawSearchRequest {
    query: string
    types?: string[]
    labels?: string[]
    start?: Date
    end?: Date
}

const SearchContext = createContext<SearchContextValue | undefined>(undefined)

export const MIN_SEARCH_LENGTH = 3
const DEBOUNCE_DURATION_MS = 500
const MAX_SEARCH_RESULTS = 5

const searchStorage = new ApiModelListStorage<SearchEntryDto, SearchResult>(
    'RECENT_SEARCHES_STORAGE',
    SearchResult
)

export const SearchDomains = { ...StructureTypes, User: 'user' }
export type SearchDomain = (typeof SearchDomains)[keyof typeof SearchDomains]

export default function ({ children }: PropsWithChildren) {
    const { searchApi, userApi, projectApi, boltConnectionApi, boltApi } = useApiContext()
    const { selectedOrganization, lazySelectedOrganizationId } = useUserContext()
    const [cycle, setCycle] = useState(0)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(false)
    const [results, setResults] = useState<SearchResult[]>([])
    const [recentSearches, setRecentSearches] = useState<SearchResult[]>(searchStorage.read())

    const [typeFilters, _filtersLoading, _filtersError] = useImmutableList(async () => {
        const orgId = selectedOrganization?.id
        if (!orgId) return null
        const response = await searchApi.getFilters({ tenantId: orgId })
        return response.data.typeFilters
    }, [selectedOrganization])

    const stopCycle = (timeoutCycle: number = cycle) => {
        clearTimeout(timeoutCycle)
        setCycle(0)
        setLoading(false)
    }

    const rawSearch = async ({ query, types, labels, start, end }: RawSearchRequest) => {
        const searchTerm = query.trim()
        if (searchTerm.length < MIN_SEARCH_LENGTH) return []

        const response = await searchApi.quickSearch({
            tenantId: lazySelectedOrganizationId,
            query: searchTerm,
            labels,
            startDate: start?.toISOString(),
            endDate: end?.toISOString(),
            types: types ?? typeFilters,
        })

        return mapModelList(response.data, SearchResult)
    }

    const debouncedSearch = (
        searchTerm: string,
        filters?: string[],
        labels?: string[],
        start?: Date,
        end?: Date
    ) => {
        if (cycle) clearTimeout(cycle)
        setLoading(true)
        setError(false)
        const thisCycle = window.setTimeout(async () => {
            const query = searchTerm.trim()
            if (query.length < MIN_SEARCH_LENGTH) return stopCycle(thisCycle)

            try {
                const response = await searchApi.quickSearch({
                    tenantId: lazySelectedOrganizationId,
                    query,
                    labels,
                    startDate: start?.toISOString(),
                    endDate: end?.toISOString(),
                    types: (filters?.length ?? 0) <= 0 ? typeFilters : filters,
                })

                const results = mapModelList(response.data, SearchResult)
                setResults(results)
            } catch {
                setError(true)
            }
            stopCycle(thisCycle)
        }, DEBOUNCE_DURATION_MS)
        setCycle(thisCycle)
    }

    const resetSearch = () => setResults([])

    const syncRecentSearches = async (result: SearchResult): Promise<SearchResult | undefined> => {
        switch (result.domainType) {
            case SearchDomains.User: {
                const response = await userApi.tenantUserGetV2({
                    userIdpId: result.domainId,
                    tenantId: lazySelectedOrganizationId,
                })
                if (!ok(response)) return undefined
                const entry = {
                    domainId: response.data.id,
                    aggregateId: response.data.id,
                    name: response.data.name,
                    domainType: SearchDomains.User,
                    date: result.date?.toISOString() ?? new Date().toISOString(),
                }
                return new SearchResult(entry)
            }
            case SearchDomains.Project: {
                const response = await projectApi.getProjectV3({
                    projectId: result.domainId,
                    tenantId: lazySelectedOrganizationId,
                })
                if (!ok(response)) return undefined
                const entry = {
                    domainId: response.data.id ?? '',
                    aggregateId: response.data.aggregateId ?? '',
                    name: response.data.name ?? '',
                    domainType: SearchDomains.Project,
                    date: response.data.createdAt,
                }
                return new SearchResult(entry)
            }
            case SearchDomains.BoltConnection: {
                const response = await boltConnectionApi.getBoltConnectionV3({
                    boltConnectionId: result.domainId,
                    tenantId: lazySelectedOrganizationId,
                })
                if (!ok(response)) return undefined
                const entry = {
                    domainId: response.data.id ?? '',
                    aggregateId: response.data.aggregateId ?? '',
                    name: response.data.name ?? '',
                    domainType: SearchDomains.BoltConnection,
                    date: response.data.createdAt,
                }
                return new SearchResult(entry)
            }
            case SearchDomains.Bolt: {
                const response = await boltApi.getBoltV2({
                    boltId: result.domainId,
                    tenantId: lazySelectedOrganizationId,
                })
                if (!ok(response)) return undefined
                const entry = {
                    domainId: response.data.id,
                    aggregateId: response.data.aggregateId,
                    name: new Bolt(response.data).name,
                    domainType: SearchDomains.Bolt,
                    date: response.data.createdAt,
                }
                return new SearchResult(entry)
            }
            default:
                return result
        }
    }

    const cleanupRecentSearches = async (id?: string) => {
        if (id && !recentSearches.find(({ domainId }) => domainId === id)) return
        const orgId = selectedOrganization?.id
        if (!(orgId && recentSearches.length)) return
        const synced = await Promise.all(recentSearches.map((r) => syncRecentSearches(r)))
        const updated = synced.filter((r) => !!r)
        const currentData = JSON.stringify(recentSearches.map((r) => r.data))
        const updatedData = JSON.stringify(updated.map((r) => r.data))
        if (currentData === updatedData) return
        searchStorage.store(updated)
        setRecentSearches(updated)
    }

    // Run cleanup once when the app starts.
    useEffect(() => {
        cleanupRecentSearches()
    }, [])

    const storeRecentSearch = (result: SearchResult) => {
        // We always store searches when clicked, but those already in the list are removed and re-appended.
        const existing = recentSearches.filter((r) => r.domainId !== result.domainId)
        const updated = [result, ...existing].slice(0, MAX_SEARCH_RESULTS)
        searchStorage.store(updated)
        setRecentSearches(updated)
    }

    const deleteRecentSearch = (result: SearchResult) => {
        const updated = recentSearches.filter((r) => r !== result)
        searchStorage.store(updated)
        setRecentSearches(updated)
    }

    const searchResults = results.slice(0, MAX_SEARCH_RESULTS)

    const value: SearchContextValue = {
        typeFilters,
        debouncedSearch,
        rawSearch,
        resetSearch,
        recentSearches,
        storeRecentSearch,
        deleteRecentSearch,
        cleanupRecentSearches,
        searchResults,
        loading,
        error,
    }

    return <SearchContext.Provider value={value}>{children}</SearchContext.Provider>
}

export const useSearchContext = () => useProvideContext(SearchContext, 'Search')
